import React, { useState } from 'react';
import GamePerformanceChart from '../../../components/GamePerformanceChart';
import BestGamesList from '../../../components/BestGamesList';
import { useTheme } from '../../../contexts/ThemeContext';

// Definir a interface para os jogos do filtro, correspondendo à de PlayedGamesPage
interface GameForFilter {
  game_id: string;
  name: string | undefined;
}

interface StatisticsViewProps {
  userId: string;
  gamesForFilter: GameForFilter[]; // Atualizado para usar a nova prop e tipo
}

const StatisticsView: React.FC<StatisticsViewProps> = ({ userId, gamesForFilter }) => {
  const { theme } = useTheme();
  const [chartType, setChartType] = useState<'line' | 'bar' | 'pie'>('line');
  const [chartInterval, setChartInterval] = useState<'week' | 'month' | 'year'>('month');
  const [selectedGameForStats, setSelectedGameForStats] = useState<string | undefined>(undefined);

  const handleChartTypeChange = (type: 'line' | 'bar' | 'pie') => {
    setChartType(type);
  };

  const handleIntervalChange = (interval: 'week' | 'month' | 'year') => {
    setChartInterval(interval);
  };

  const handleSelectedGameChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedGameForStats(e.target.value === '' ? undefined : e.target.value);
  };

  // Classes para botões de filtro ativos e inativos, adaptadas para o tema
  const getButtonClasses = (isActive: boolean) => {
    if (isActive) {
      return theme === 'dark' 
        ? 'bg-primary text-primary-foreground' 
        : 'bg-indigo-100 text-indigo-700';
    }
    return theme === 'dark'
      ? 'bg-background text-foreground hover:bg-muted'
      : 'bg-white text-gray-700 hover:bg-gray-100';
  };
  
  // Classes para bordas, adaptadas para o tema
  const getBorderClasses = () => {
    return theme === 'dark' ? 'border-border' : 'border-gray-300';
  };

  // Classes para inputs/selects, adaptadas para o tema
  const getInputSelectClasses = () => {
    return theme === 'dark'
      ? `bg-input border-border text-foreground placeholder:text-muted-foreground focus:ring-ring focus:border-ring`
      : `border-gray-300 focus:ring-indigo-500 focus:border-indigo-500`;
  };

  return (
    <div className={`space-y-6 ${theme === 'dark' ? 'text-foreground' : 'text-gray-900'}`}>
      {/* Filtros do Gráfico */}
      <div className="flex flex-col md:flex-row gap-3 justify-between mb-4">
        <div className="w-full md:w-1/3">
          <label htmlFor="game-filter-stats" className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-muted-foreground' : 'text-gray-700'}`}>
            Filtrar por Jogo
          </label>
          <select
            id="game-filter-stats"
            value={selectedGameForStats || ''}
            onChange={handleSelectedGameChange}
            className={`w-full p-2 border rounded-md ${getInputSelectClasses()}`}
          >
            <option value="" className={theme === 'dark' ? 'bg-input text-foreground' : ''}>Todos os jogos</option>
            {gamesForFilter.map(game => (
              <option 
                key={game.game_id} 
                value={game.game_id} 
                className={theme === 'dark' ? 'bg-input text-foreground' : ''}
              >
                {game.name || 'Jogo Desconhecido'}
              </option>
            ))}
          </select>
        </div>
        <div className="w-full md:w-1/3">
          <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-muted-foreground' : 'text-gray-700'}`}>
            Tipo de Gráfico
          </label>
          <div className={`flex border rounded-md overflow-hidden ${getBorderClasses()}`}>
            <button
              onClick={() => handleChartTypeChange('line')}
              className={`flex-1 py-2 px-1 sm:px-3 text-sm ${getButtonClasses(chartType === 'line')}`}
            >
              Linha
            </button>
            <button
              onClick={() => handleChartTypeChange('bar')}
              className={`flex-1 py-2 px-1 sm:px-3 text-sm border-l ${getBorderClasses()} ${getButtonClasses(chartType === 'bar')}`}
            >
              Barras
            </button>
            <button
              onClick={() => handleChartTypeChange('pie')}
              className={`flex-1 py-2 px-1 sm:px-3 text-sm border-l ${getBorderClasses()} ${getButtonClasses(chartType === 'pie')}`}
            >
              Pizza
            </button>
          </div>
        </div>
        <div className="w-full md:w-1/3">
          <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-muted-foreground' : 'text-gray-700'}`}>
            Intervalo
          </label>
          <div className={`flex border rounded-md overflow-hidden ${getBorderClasses()}`}>
            <button
              onClick={() => handleIntervalChange('week')}
              className={`flex-1 py-2 px-1 sm:px-3 text-sm ${getButtonClasses(chartInterval === 'week')}`}
            >
              Semanal
            </button>
            <button
              onClick={() => handleIntervalChange('month')}
              className={`flex-1 py-2 px-1 sm:px-3 text-sm border-l ${getBorderClasses()} ${getButtonClasses(chartInterval === 'month')}`}
            >
              Mensal
            </button>
            <button
              onClick={() => handleIntervalChange('year')}
              className={`flex-1 py-2 px-1 sm:px-3 text-sm border-l ${getBorderClasses()} ${getButtonClasses(chartInterval === 'year')}`}
            >
              Anual
            </button>
          </div>
        </div>
      </div>

      {/* Gráfico e Lista de Melhores Jogos */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className={`lg:col-span-2 p-3 rounded-lg shadow overflow-hidden ${theme === 'dark' ? 'bg-card text-card-foreground' : 'bg-white'}`}>
          <GamePerformanceChart
            userId={userId}
            gameId={selectedGameForStats}
            chartType={chartType}
            interval={chartInterval}
            height={350}
            showScores={!!selectedGameForStats} // Mostra scores apenas se um jogo específico for selecionado
          />
        </div>
        <div className={`lg:col-span-1 rounded-lg shadow overflow-hidden ${theme === 'dark' ? 'bg-card text-card-foreground' : 'bg-white'}`}>
          <BestGamesList 
            userId={userId} 
            minPlays={3} 
          />
        </div>
      </div>
    </div>
  );
};

export default StatisticsView; 